/**
 * 이미지 업로드 헬퍼 유틸리티
 * base64 이미지를 서버에 업로드하여 URL로 변환
 */

/**
 * base64 문자열을 Blob으로 변환
 * @param {string} base64 - base64 인코딩된 이미지 문자열 (data:image/png;base64,...)
 * @returns {Blob} - Blob 객체
 */
const base64ToBlob = (base64) => {
  // data:image/png;base64,iVBORw0KG... 형식에서 실제 데이터만 추출
  const parts = base64.split(',');
  const contentType = parts[0].match(/:(.*?);/)[1];
  const raw = atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};

/**
 * Blob을 File 객체로 변환
 * @param {Blob} blob - Blob 객체
 * @param {string} filename - 파일명
 * @returns {File} - File 객체
 */
const blobToFile = (blob, filename) => {
  return new File([blob], filename, { type: blob.type });
};

/**
 * 이미지를 서버에 업로드
 * @param {File} file - 업로드할 파일
 * @param {string} authToken - JWT 인증 토큰
 * @returns {Promise<string>} - 업로드된 이미지 URL
 */
const uploadImageToServer = async (file, authToken) => {
  const formData = new FormData();
  formData.append('file', file);

  // travel-api를 사용
  const apiUrl = `/travel-api/api/v1/images/upload`;

  console.log('📤 [ImageUploadHelper] 이미지 업로드 시작 (travel-api):', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
  });

  const headers = {
    'Accept': 'application/json'
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  } else {
    throw new Error('인증 토큰이 없습니다.');
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: headers,
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ [ImageUploadHelper] 업로드 실패:', {
      status: response.status,
      statusText: response.statusText,
      errorText
    });
    throw new Error(`이미지 업로드에 실패했습니다: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  console.log('✅ [ImageUploadHelper] 업로드 성공:', result);

  // 서버 응답에서 file_url 사용
  if (result.success && result.data && result.data.file_url) {
    const imageUrl = result.data.file_url;

    console.log('🔄 [ImageUploadHelper] 이미지 URL:', imageUrl);

    return imageUrl;
  } else {
    throw new Error('서버에서 이미지 URL을 반환하지 않았습니다.');
  }
};

/**
 * HTML 문자열 내의 모든 base64 이미지를 서버 URL로 변환
 * @param {string} htmlContent - HTML 문자열 (RichTextEditor 내용)
 * @param {string} authToken - JWT 인증 토큰
 * @returns {Promise<string>} - 변환된 HTML 문자열
 */
export const convertBase64ImagesToUrls = async (htmlContent, authToken) => {
  if (!htmlContent) {
    return htmlContent;
  }

  // base64 이미지를 찾는 정규식
  // <img src="data:image/...;base64,..." /> 형식
  const base64ImageRegex = /<img[^>]+src="(data:image\/[^;]+;base64,[^"]+)"[^>]*>/g;

  const matches = [];
  let match;

  // 모든 base64 이미지 찾기
  while ((match = base64ImageRegex.exec(htmlContent)) !== null) {
    matches.push({
      fullMatch: match[0],
      base64Src: match[1]
    });
  }

  if (matches.length === 0) {
    console.log('ℹ️ [ImageUploadHelper] base64 이미지가 없습니다.');
    return htmlContent;
  }

  console.log(`🔍 [ImageUploadHelper] ${matches.length}개의 base64 이미지를 찾았습니다.`);

  let convertedHtml = htmlContent;
  let uploadedCount = 0;

  // 각 base64 이미지를 서버에 업로드
  for (const { fullMatch, base64Src } of matches) {
    try {
      // base64를 Blob으로 변환
      const blob = base64ToBlob(base64Src);

      // Blob을 File로 변환 (타임스탬프를 파일명으로 사용)
      const timestamp = Date.now();
      const extension = blob.type.split('/')[1] || 'png';
      const filename = `image-${timestamp}-${uploadedCount}.${extension}`;
      const file = blobToFile(blob, filename);

      // 서버에 업로드
      const imageUrl = await uploadImageToServer(file, authToken);

      // HTML에서 base64 src를 서버 URL로 교체
      const newImgTag = fullMatch.replace(base64Src, imageUrl);
      convertedHtml = convertedHtml.replace(fullMatch, newImgTag);

      uploadedCount++;
      console.log(`✅ [ImageUploadHelper] 이미지 ${uploadedCount}/${matches.length} 변환 완료:`, imageUrl);
    } catch (error) {
      console.error('❌ [ImageUploadHelper] 이미지 변환 실패:', error);
      // 실패한 이미지는 그대로 두고 계속 진행
    }
  }

  console.log(`🎉 [ImageUploadHelper] 총 ${uploadedCount}/${matches.length}개 이미지 변환 완료`);

  return convertedHtml;
};

/**
 * 객체의 특정 필드들에서 base64 이미지를 URL로 변환
 * @param {Object} obj - 변환할 객체
 * @param {string[]} fields - 변환할 필드 이름 배열 (예: ['memo', 'description'])
 * @param {string} authToken - JWT 인증 토큰
 * @returns {Promise<Object>} - 변환된 객체
 */
export const convertObjectBase64Images = async (obj, fields, authToken) => {
  const convertedObj = { ...obj };

  for (const field of fields) {
    if (convertedObj[field]) {
      convertedObj[field] = await convertBase64ImagesToUrls(convertedObj[field], authToken);
    }
  }

  return convertedObj;
};

export default {
  convertBase64ImagesToUrls,
  convertObjectBase64Images,
};
